// src/pages/InspeccionSalida.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 📸 INSPECCIÓN DE SALIDA - SISTEMA RENTACAR eFIT
// Registro del estado del vehículo al momento de entrega al cliente
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import api from "../services/api";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const descargarContratoPDF = async (rentaId, numero_contrato) => {
  try {
    const res = await api.get(`/rencar/rentas/${rentaId}/contrato`, { responseType: 'blob' });
    const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `contrato-${numero_contrato || 'RC-' + String(rentaId).padStart(4, '0')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo descargar el contrato PDF', confirmButtonColor: '#3b82f6' });
  }
};

const InspeccionSalida = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [renta, setRenta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const [form, setForm] = useState({
    gasolina: "",
    kilometraje: "",
    rayaduras: "",
    llantas: "excelente",
    accesorios: {
      gato: true,
      llave_ruedas: true,
      triangulo: true,
      botiquin: false,
      extintor: false,
    }
  });

  const [fotos, setFotos] = useState([]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 CARGAR DATOS DE LA RENTA
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const cargarDatos = async () => {
      if (!id) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "ID de renta inválido",
          confirmButtonColor: "#3b82f6"
        });
        navigate("/rencar");
        return;
      }

      try {
        setCargando(true);
        const res = await api.get(`/rencar/rentas/${id}`);
        
        if (res.data.ok && res.data.renta) {
          setRenta(res.data.renta);
          
          // Verificar estado
          if (res.data.renta.estado !== "abierta") {
            Swal.fire({
              icon: "warning",
              title: "Renta no disponible",
              text: `Esta renta está en estado: ${res.data.renta.estado}`,
              confirmButtonColor: "#3b82f6"
            }).then(() => navigate("/rencar"));
          }
        }
      } catch (error) {
        console.error("❌ Error cargando renta:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar la información de la renta",
          confirmButtonColor: "#3b82f6"
        });
        navigate("/rencar");
      } finally {
        setCargando(false);
      }
    };

    cargarDatos();
  }, [id, navigate]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📸 MANEJO DE FOTOS
  // ═══════════════════════════════════════════════════════════════════════════
  const onFotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + fotos.length > 6) {
      Swal.fire({
        icon: "warning",
        title: "Límite de fotos",
        text: "Máximo 6 fotos permitidas",
        confirmButtonColor: "#3b82f6"
      });
      return;
    }
    setFotos([...fotos, ...files]);
  };

  const eliminarFoto = (index) => {
    setFotos(fotos.filter((_, i) => i !== index));
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 💾 GUARDAR INSPECCIÓN
  // ═══════════════════════════════════════════════════════════════════════════
  const guardarInspeccion = async () => {
    // Validaciones
    if (!form.gasolina || !form.kilometraje) {
      return Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Nivel de gasolina y kilometraje son obligatorios",
        confirmButtonColor: "#3b82f6"
      });
    }

    if (parseFloat(form.kilometraje) <= 0) {
      return Swal.fire({
        icon: "warning",
        title: "Kilometraje inválido",
        text: "El kilometraje debe ser mayor a 0",
        confirmButtonColor: "#3b82f6"
      });
    }

    const payload = {
      gasolina: form.gasolina,
      kilometraje: parseFloat(form.kilometraje),
      rayaduras: form.rayaduras || "Ninguna observación",
      fotos: fotos.map(f => f.name).join(",")
    };

    setGuardando(true);
    try {
      const res = await api.post(`/rencar/rentas/${id}/inspeccion-salida`, payload);

      if (res.data.ok) {
        Swal.fire({
          icon: "success",
          title: "✅ Inspección Completada",
          html: `
            <div style="text-align: left; padding: 10px;">
              <p><strong>La renta está lista para cobrar en el POS</strong></p>
              <p>Estado: <span style="color: #3b82f6; font-weight: bold;">EN RENTA</span></p>
              <p>Kilometraje inicial: ${form.kilometraje} km</p>
              <p>Gasolina: ${form.gasolina}</p>
            </div>
          `,
          confirmButtonText: "Ir al Dashboard",
          confirmButtonColor: "#10b981",
          allowOutsideClick: false
        }).then(() => {
          navigate("/rencar");
        });
      }
    } catch (error) {
      console.error("❌ Error guardando inspección:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.msg || "No se pudo guardar la inspección",
        confirmButtonColor: "#3b82f6"
      });
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Cargando datos de la renta...</p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.pageTitle}>
          <span style={styles.pageTitleIcon}>📸</span>
          <div>
            <h1 style={styles.pageTitleText}>Inspección de Salida</h1>
            <p style={styles.pageTitleSub}>
              Renta #{id} • {renta?.Vehiculo?.marca} {renta?.Vehiculo?.modelo} 
              <span style={styles.placaBadge}>{renta?.Vehiculo?.placa}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div style={styles.infoCard}>
        <div style={styles.infoRow}>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Cliente:</span>
            <span style={styles.infoValue}>{renta?.Cliente?.nombre}</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Salida:</span>
            <span style={styles.infoValue}>
              {new Date(renta?.fecha_salida).toLocaleDateString("es-DO")}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Retorno:</span>
            <span style={styles.infoValue}>
              {new Date(renta?.fecha_retorno_programada).toLocaleDateString("es-DO")}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Días:</span>
            <span style={styles.infoValue}>{renta?.dias}</span>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div style={styles.formCard}>
        {/* Sección 1: Datos Principales */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📊 Estado del Vehículo</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Kilometraje Actual *</label>
              <input
                type="number"
                style={styles.input}
                placeholder="Ej: 15400"
                value={form.kilometraje}
                onChange={(e) => setForm({ ...form, kilometraje: e.target.value })}
                min="0"
                step="1"
              />
              <span style={styles.inputHint}>Registre el kilometraje exacto del odómetro</span>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Nivel de Gasolina *</label>
              <select
                style={styles.input}
                value={form.gasolina}
                onChange={(e) => setForm({ ...form, gasolina: e.target.value })}
              >
                <option value="">Seleccione...</option>
                <option value="lleno">🟢 Lleno (4/4)</option>
                <option value="3/4">🟡 Tres Cuartos (3/4)</option>
                <option value="1/2">🟠 Medio (1/2)</option>
                <option value="1/4">🔴 Un Cuarto (1/4)</option>
                <option value="vacio">⚫ Vacío</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Estado de Llantas</label>
              <select
                style={styles.input}
                value={form.llantas}
                onChange={(e) => setForm({ ...form, llantas: e.target.value })}
              >
                <option value="excelente">✅ Excelente</option>
                <option value="buenas">👍 Buenas</option>
                <option value="regulares">⚠️ Regulares</option>
                <option value="necesita_cambio">❌ Necesita Cambio</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sección 2: Accesorios */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🔧 Accesorios Entregados</h3>
          <div style={styles.checklistGrid}>
            {Object.entries(form.accesorios).map(([key, value]) => (
              <label key={key} style={styles.checkItem}>
                <input
                  type="checkbox"
                  checked={value}
                  onChange={() => setForm({
                    ...form,
                    accesorios: { ...form.accesorios, [key]: !value }
                  })}
                  style={styles.checkbox}
                />
                <span>{key.replace("_", " ").toUpperCase()}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Sección 3: Observaciones */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📝 Observaciones y Daños Visibles</h3>
          <textarea
            style={styles.textarea}
            value={form.rayaduras}
            onChange={(e) => setForm({ ...form, rayaduras: e.target.value })}
            placeholder="Describa rayones, abolladuras o cualquier daño visible en la carrocería..."
            rows="4"
          />
        </div>

        {/* Sección 4: Fotos */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📷 Evidencia Fotográfica (Máx. 6)</h3>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={onFotoChange}
            style={styles.fileInput}
          />
          
          {fotos.length > 0 && (
            <div style={styles.previewGrid}>
              {fotos.map((foto, i) => (
                <div key={i} style={styles.previewItem}>
                  <img
                    src={URL.createObjectURL(foto)}
                    alt={`preview-${i}`}
                    style={styles.previewImg}
                  />
                  <button
                    style={styles.btnDeleteFoto}
                    onClick={() => eliminarFoto(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <span style={styles.inputHint}>
            📌 Tome fotos de los 4 lados del vehículo, tablero y maletero
          </span>
        </div>

        {/* Botones de Acción */}
        <div style={styles.formActions}>
          <button
            style={styles.btnSecondary}
            onClick={() => navigate("/rencar")}
            disabled={guardando}
          >
            ← Cancelar
          </button>
          <button
            style={{ ...styles.btnSecondary, borderColor: '#3b82f6', color: '#3b82f6' }}
            onClick={() => descargarContratoPDF(id, renta?.numero_contrato)}
            disabled={guardando}
            title="Descargar contrato antes de la inspección"
          >
            📄 Ver Contrato
          </button>
          <button
            style={styles.btnPrimary}
            onClick={guardarInspeccion}
            disabled={guardando || !form.gasolina || !form.kilometraje}
          >
            {guardando ? (
              <div style={styles.btnLoading}>
                <div style={styles.btnSpinner}></div>
                <span>Guardando...</span>
              </div>
            ) : (
              <>
                <span style={styles.btnIcon}>✓</span>
                <span>Completar Inspección</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Animaciones */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: "calc(100vh - 70px)",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    padding: "24px 32px",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },

  // Page Header
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px"
  },
  pageTitle: {
    display: "flex",
    alignItems: "center",
    gap: "16px"
  },
  pageTitleIcon: {
    fontSize: "36px",
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    padding: "14px",
    borderRadius: "16px",
    border: "2px solid #93c5fd"
  },
  pageTitleText: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#1e293b",
    margin: 0
  },
  pageTitleSub: {
    fontSize: "14px",
    color: "#64748b",
    margin: "4px 0 0 0",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  placaBadge: {
    background: "#1e293b",
    color: "white",
    padding: "2px 10px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "700",
    marginLeft: "8px"
  },

  // Info Card
  infoCard: {
    background: "white",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "24px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0"
  },
  infoRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "20px"
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  infoLabel: {
    fontSize: "12px",
    color: "#64748b",
    fontWeight: "600",
    textTransform: "uppercase"
  },
  infoValue: {
    fontSize: "16px",
    color: "#1e293b",
    fontWeight: "700"
  },

  // Form Card
  formCard: {
    background: "white",
    borderRadius: "20px",
    padding: "32px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid #e2e8f0",
    maxWidth: "1000px",
    margin: "0 auto"
  },

  // Sections
  section: {
    marginBottom: "32px",
    paddingBottom: "24px",
    borderBottom: "1px solid #f1f5f9"
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "20px"
  },

  // Form Elements
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px"
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#475569"
  },
  input: {
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    outline: "none",
    background: "#f8fafc",
    transition: "all 0.2s",
    fontFamily: "inherit"
  },
  textarea: {
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    outline: "none",
    background: "#f8fafc",
    resize: "vertical",
    fontFamily: "inherit"
  },
  inputHint: {
    fontSize: "12px",
    color: "#94a3b8",
    fontStyle: "italic"
  },

  // Checklist
  checklistGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    background: "#f8fafc",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0"
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    padding: "4px"
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer"
  },

  // Fotos
  fileInput: {
    padding: "12px",
    border: "2px dashed #cbd5e1",
    borderRadius: "12px",
    background: "#f8fafc",
    cursor: "pointer",
    marginBottom: "16px"
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: "12px",
    marginBottom: "12px"
  },
  previewItem: {
    position: "relative",
    aspectRatio: "1",
    borderRadius: "12px",
    overflow: "hidden",
    border: "2px solid #3b82f6"
  },
  previewImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  btnDeleteFoto: {
    position: "absolute",
    top: "4px",
    right: "4px",
    background: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold"
  },

  // Buttons
  formActions: {
    display: "flex",
    gap: "16px",
    justifyContent: "flex-end",
    marginTop: "32px",
    paddingTop: "24px",
    borderTop: "2px solid #f1f5f9"
  },
  btnPrimary: {
    padding: "14px 32px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  btnSecondary: {
    padding: "14px 32px",
    background: "#f1f5f9",
    color: "#475569",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer"
  },
  btnIcon: {
    fontSize: "18px"
  },
  btnLoading: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  btnSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },

  // Loading
  loadingContainer: {
    minHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },
  loadingText: {
    marginTop: "16px",
    color: "#64748b",
    fontSize: "16px",
    fontWeight: "600"
  }
};

export default InspeccionSalida;